// src/pages/ProfilePage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchMyProfile,
  changePassword,
  clearError as clearProfileError,
} from '../../store/slices/profileSlice';
import profileApi from '../../api/profileApi';
import { Camera, Eye, EyeOff, Loader2 } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import { Button } from '../../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { cn } from 'lib/utils';

const ProfilePage = () => {
  const dispatch = useDispatch();
  const { profile, isLoading, error } = useSelector((state) => state.profile);
  const { user } = useSelector((state) => state.auth);

  // Local state
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  // Password
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Security Answers
  const [securityAnswers, setSecurityAnswers] = useState([]);
  const [answers, setAnswers] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [answersLoading, setAnswersLoading] = useState(true);

  const isPasswordValid = (password) => {
    return password.length >= 8 &&
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /\d/.test(password);
  };

  // Load profile
  useEffect(() => {
    if (user?.id) {
      dispatch(fetchMyProfile());
    }
  }, [dispatch, user?.id]);

  // Load security answers
  // useEffect(() => {
  //   const loadSecurityAnswers = async () => {
  //     if (!user?.id) return;
  //     setAnswersLoading(true);
  //     try {
  //       const data = await profileApi.getSecurityAnswers(user.id);
  //       setSecurityAnswers(data);
  //       const map = {};
  //       data.forEach((item) => {
  //         map[item.id] = item.answer || '';
  //       });
  //       setAnswers(map);
  //     } catch (err) {
  //       console.error('Failed to load security answers:', err);
  //       toast.error('Failed to load security questions');
  //     } finally {
  //       setAnswersLoading(false);
  //     }
  //   };

  //   loadSecurityAnswers();
  // }, [user?.id]);

  // Sync image preview
  useEffect(() => {
    if (profile?.profile_img) {
      setImagePreview(profile.profile_img);
    }
  }, [profile?.profile_img]);

  // Detect changes
  useEffect(() => {
    const changed = securityAnswers.some(
      (item) => (answers[item.id] || '').trim() !== (item.answer || '').trim()
    );
    setHasChanges(changed);
  }, [answers, securityAnswers]);

  // Show error toast
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearProfileError());
    }
  }, [error, dispatch]);

  // Image Upload
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);

    uploadImage(file);
  };

  const uploadImage = async (file) => {
    if (!user?.id) return;
    try {
      const updated = await profileApi.updateProfile(user.id, file);
      setImagePreview(updated.profile_img || null);
      toast.success('Profile photo updated successfully');
      dispatch(fetchMyProfile());
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to upload image');
      setImagePreview(profile?.profile_img || null);
    }
  };

  // Save Security Answers — FIXED: Now sends employee + question
  const handleSaveAnswers = async () => {
    const payload = securityAnswers
      .filter((item) => {
        const newAns = (answers[item.id] || '').trim();
        const oldAns = (item.answer || '').trim();
        return newAns && newAns !== oldAns;
      })
      .map((item) => ({
        id: item.id,
        answer: (answers[item.id] || '').trim(),
        employee: item.employee,        // Required by backend
        question: item.question,        // Required by backend
      }));

    if (payload.length === 0) {
      toast.info('No changes to save');
      return;
    }

    try {
      await profileApi.bulkUpdateAnswers(payload);
      toast.success('Security answers saved successfully');

      // Reload fresh data
      const refreshed = await profileApi.getSecurityAnswers(user.id);
      setSecurityAnswers(refreshed);
      const map = {};
      refreshed.forEach((a) => (map[a.id] = a.answer || ''));
      setAnswers(map);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save answers');
    }
  };

  // Change Password
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    const result = await dispatch(
      changePassword({
        old_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
        confirm_password: passwordData.confirmPassword,
      })
    );

    if (changePassword.fulfilled.match(result)) {
      toast.success('Password changed successfully');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    }
  };

  if (isLoading && !profile) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex h-screen items-center justify-center text-muted-foreground">
        Unable to load profile
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="My Profile" description="Manage your personal information and security settings" />

      {/* PREMIUM WALLET CARD - CRED STYLE */}
      <div className="relative mx-auto -mt-8 mb-10">
        <div className="relative overflow-hidden rounded-2xl shadow-2xl bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 p-1">
          {/* Shiny overlay effect */}
          <div className="absolute inset-0 bg-white opacity-20 translate-x-[-70%] translate-y-[-50%] w-[200%] h-[200%] animate-shine"></div>

          {/* Card Content */}
          <div className="relative bg-gray-900/95 backdrop-blur-xl rounded-2xl p-8 text-white">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-purple-300 text-sm font-medium tracking-wider">CANTEEN WALLET</p>
                <h2 className="text-4xl font-bold mt-2 tracking-tight">
                  ₹{Number(profile?.wallet_balance || 0).toLocaleString('en-IN', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </h2>
                <p className="text-purple-200 text-sm mt-2 opacity-90">
                  Available Balance
                </p>
              </div>

              {/* Chip + Contactless Icon */}
              <div className="text-right">
                <div className="bg-yellow-400/20 backdrop-blur-sm border border-yellow-400/40 rounded-lg p-4 inline-block">
                  <div className="w-12 h-8 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-md shadow-lg"></div>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-end">
              <div>
                <p className="text-xs text-purple-300 uppercase tracking-widest">Employee</p>
                <p className="text-lg font-medium mt-1">{profile?.full_name || 'User'}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-purple-300 uppercase tracking-widest">Code</p>
                <p className="text-lg font-bold">{profile?.code || 'N/A'}</p>
              </div>
            </div>

            {/* Glow dots decoration */}
            <div className="absolute top-4 right-4 w-20 h-20 bg-purple-500/30 rounded-full blur-3xl"></div>
            <div className="absolute bottom-6 left-10 w-32 h-32 bg-pink-500/20 rounded-full blur-3xl"></div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* PERSONAL INFO */}
        <Card>
          <CardHeader>
            <CardTitle>Personal Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col items-center space-y-4">
              <Avatar className="h-32 w-32 ring-4 ring-background">
                <AvatarImage src={imagePreview || profile.profile_img} className="object-cover" />
                <AvatarFallback className="text-3xl bg-gradient-to-br from-primary to-accent text-white">
                  {profile.full_name?.charAt(0).toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>

              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={isLoading}>
                <Camera className="mr-2 h-4 w-4" />
                {isLoading ? 'Uploading…' : 'Change Photo'}
              </Button>
            </div>

            <div className="space-y-4">
              <div className="grid gap-2">
                <Label>Employee Code</Label>
                <Input value={profile.code || ''} disabled />
              </div>
              <div className="grid gap-2">
                <Label>Email</Label>
                <Input value={profile.email || ''} disabled />
              </div>
              <div className="grid gap-2">
                <Label>Full Name</Label>
                <Input value={profile.full_name || ''} disabled />
              </div>
              <div className="grid gap-2">
                <Label>Role</Label>
                <Input value={profile.role_name || 'N/A'} disabled />
              </div>
              <div className="grid gap-2">
                <Label>Department</Label>
                <Input value={profile.department_name || 'N/A'} disabled />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CHANGE PASSWORD */}
        {/* <Card>
          <CardHeader>
            <CardTitle>Change Password</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div className="relative">
                <Label>Current Password</Label>
                <Input
                  type={showCurrent ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-9 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowCurrent(!showCurrent)}
                >
                  {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              <div className="relative">
                <Label>New Password</Label>
                <Input
                  type={showNew ? 'text' : 'password'}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  className="absolute right-3 top-9 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowNew(!showNew)}
                >
                  {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              <div className="relative">
                <Label>Confirm New Password</Label>
                <Input
                  type={showConfirm ? 'text' : 'password'}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-9 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowConfirm(!showConfirm)}
                >
                  {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Changing Password...
                  </>
                ) : (
                  'Change Password'
                )}
              </Button>
            </form>
          </CardContent>
        </Card> */}
        {/* CHANGE PASSWORD */}
        {/* <Card>
          <CardHeader>
            <CardTitle>Change Password</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordChange} className="space-y-5">
             
              <div className="relative">
                <Label>Current Password</Label>
                <Input
                  type={showCurrent ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  required
                  className={cn(
                    "pr-10",
                    !passwordData.currentPassword && passwordData.currentPassword !== '' && "border-destructive focus-visible:ring-destructive"
                  )}
                />
                <button
                  type="button"
                  className="absolute right-3 top-9 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowCurrent(!showCurrent)}
                >
                  {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              <div className="space-y-1">
                <Label>New Password</Label>
                <div className="relative">
                  <Input
                    type={showNew ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    required
                    minLength={6}
                    className={cn(
                      "pr-10",
                      passwordData.newPassword && passwordData.newPassword.length < 6 && "border-destructive focus-visible:ring-destructive"
                    )}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowNew(!showNew)}
                  >
                    {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {passwordData.newPassword && passwordData.newPassword.length < 6 && (
                  <p className="text-sm text-destructive font-medium">
                    Password must be at least 6 characters
                  </p>
                )}
              </div>


              <div className="space-y-1">
                <Label>Confirm New Password</Label>
                <div className="relative">
                  <Input
                    type={showConfirm ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    required
                    className={cn(
                      "pr-10",
                      passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && "border-destructive focus-visible:ring-destructive"
                    )}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowConfirm(!showConfirm)}
                  >
                    {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                  <p className="text-sm text-destructive font-medium">
                    Passwords do not match
                  </p>
                )}
              </div>

                     <div className="p-3 rounded-lg bg-muted/50 border border-border">
                <p className="text-xs font-semibold mb-2">Password must contain:</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• At least 8 characters</li>
                  <li>• One uppercase letter</li>
                  <li>• One lowercase letter</li>
                  <li>• One number</li>
                </ul>
              </div>

            
              <Button
                type="submit"
                className="w-full"
                disabled={
                  isLoading ||
                  !passwordData.currentPassword ||
                  passwordData.newPassword.length < 6 ||
                  passwordData.newPassword !== passwordData.confirmPassword
                }
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Changing Password...
                  </>
                ) : (
                  'Change Password'
                )}
              </Button>
            </form>
          </CardContent>
        </Card> */}

        {/* CHANGE PASSWORD */}
        <Card>
          <CardHeader>
            <CardTitle>Change Password</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordChange} className="space-y-5">
              {/* Current Password */}
              <div className="relative">
                <Label>Current Password</Label>
                <Input
                  type={showCurrent ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  className="absolute right-3 top-9 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowCurrent(!showCurrent)}
                >
                  {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              {/* New Password */}
              <div className="space-y-2">
                <Label>New Password</Label>
                <div className="relative">
                  <Input
                    type={showNew ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    required
                    className={cn(
                      "pr-10",
                      passwordData.newPassword && !isPasswordValid(passwordData.newPassword) && "border-destructive focus-visible:ring-destructive"
                    )}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowNew(!showNew)}
                  >
                    {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>

                {/* Live Password Strength Rules */}


                {passwordData.newPassword && !isPasswordValid(passwordData.newPassword) && (
                  <p className="text-sm text-destructive font-medium">
                    Password does not meet requirements
                  </p>
                )}
              </div>

              {/* Confirm Password */}
              <div className="space-y-1">
                <Label>Confirm New Password</Label>
                <div className="relative">
                  <Input
                    type={showConfirm ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    required
                    className={cn(
                      "pr-10",
                      passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && "border-destructive focus-visible:ring-destructive"
                    )}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowConfirm(!showConfirm)}
                  >
                    {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                  <p className="text-sm text-destructive font-medium">
                    Passwords do not match
                  </p>
                )}
              </div>

              <div className="p-3 rounded-lg bg-muted/50 border border-border space-y-2 text-xs">
                <p className="font-semibold text-foreground">Password must contain:</p>
                <ul className="space-y-1.5">
                  <li className={cn("flex items-center gap-2", passwordData.newPassword.length >= 8 ? "text-green-500" : "text-destructive")}>
                    {passwordData.newPassword.length >= 8 ? "✓" : "✗"} At least 8 characters
                  </li>
                  <li className={cn("flex items-center gap-2", /[A-Z]/.test(passwordData.newPassword) ? "text-green-500" : "text-destructive")}>
                    {/[A-Z]/.test(passwordData.newPassword) ? "✓" : "✗"} One uppercase letter
                  </li>
                  <li className={cn("flex items-center gap-2", /[a-z]/.test(passwordData.newPassword) ? "text-green-500" : "text-destructive")}>
                    {/[a-z]/.test(passwordData.newPassword) ? "✓" : "✗"} One lowercase letter
                  </li>
                  <li className={cn("flex items-center gap-2", /\d/.test(passwordData.newPassword) ? "text-green-500" : "text-destructive")}>
                    {/\d/.test(passwordData.newPassword) ? "✓" : "✗"} One number
                  </li>
                </ul>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full"
                disabled={
                  isLoading ||
                  !passwordData.currentPassword ||
                  !isPasswordValid(passwordData.newPassword) ||
                  passwordData.newPassword !== passwordData.confirmPassword
                }
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Changing Password...
                  </>
                ) : (
                  'Change Password'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* SECURITY QUESTIONS */}
      {/* <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Security Questions</CardTitle>
          <Button onClick={handleSaveAnswers} disabled={isLoading || answersLoading || !hasChanges}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save All Answers'
            )}
          </Button>
        </CardHeader>
        <CardContent>
          {answersLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : securityAnswers.length === 0 ? (
            <p className="text-muted-foreground">No security questions configured.</p>
          ) : (
            <div className="space-y-6">
              {securityAnswers.map((item) => (
                <div key={item.id} className="grid gap-2">
                  <Label className="font-medium">{item.question_name}</Label>
                  <Input
                    type="text"
                    Shelton
                    value={answers[item.id] || ''}
                    onChange={(e) => setAnswers((prev) => ({ ...prev, [item.id]: e.target.value }))}
                    placeholder="Enter your answer"
                  />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card> */}
    </div>
  );
};

export default ProfilePage;